import { isInitialReviewComplete } from "../checkNewRequest/checkNewRequestPageUtils";
import { WORKSPACE_SECTIONS } from "./projectWorkspaceConfig";

export const WORKSPACE_SECTION_IDS = WORKSPACE_SECTIONS.map((s) => s.id);

/** Next workspace section after initial review is approved (per product flow). */
export const SECTION_AFTER_INITIAL_REVIEW = "loan-mehrsan";

/** Maps backend WorkflowV2 stage → workspace sidebar section. */
const STAGE_TO_SECTION = {
  NoPlan: "initial-review",
  PlanCreated: "operational",
  VisitAssigned: "loan-mehrsan",
  VisitAccepted: "visit",
  VisitScheduled: "visit",
  VisitCompleted: "quotes-contract",
  QuoteSubmitted: "quotes-contract",
  QuoteApproved: "quotes-contract",
  ContractorCostApproved: "quotes-contract",
  // After price/contract issuance, stay on fee & discount panel (کارمزد و تخفیف)
  CustomerPriceDraft: "quotes-contract",
  CustomerPriceApproved: "quotes-contract",
  CustomerPriceAccepted: "quotes-contract",
  CustomerContractPendingUpload: "execution",
  CustomerContractUploaded: "execution",
  CustomerContractApproved: "execution",
  ContractGenerated: "quotes-contract",
  ContractorAccepted: "execution",
  FinalApprovalPending: "execution",
  ReadyForExecution: "execution",
  ExecutionActivated: "execution",
  ExecutionPlanApproved: "financial",
};

export const pickWorkflowField = (obj, ...keys) => {
  if (!obj) return undefined;
  for (const key of keys) {
    if (obj[key] !== undefined && obj[key] !== null) return obj[key];
  }
  return undefined;
};

const isTruthyFlag = (value) =>
  value === true || value === "true" || value === 1 || value === "1";

const normalizeConfirm = (request) =>
  String(
    request?.requestConfirm ?? request?.RequestConfirm ?? ""
  ).toLowerCase();

/** When initial review is done, advance workspace stage past initial-review. */
const resolveSectionAfterInitialReview = (sectionId, request) => {
  if (!isInitialReviewComplete(request)) {
    return sectionId;
  }
  if (sectionId === "initial-review") {
    return SECTION_AFTER_INITIAL_REVIEW;
  }
  return sectionId;
};

export const mapWorkflowStageToSectionId = (summary, request) => {
  const hasV2 = isTruthyFlag(
    pickWorkflowField(summary, "hasWorkflowV2", "HasWorkflowV2")
  );
  const stage =
    pickWorkflowField(summary, "currentWorkflowV2Stage", "CurrentWorkflowV2Stage") ||
    "";

  if (hasV2 && stage && STAGE_TO_SECTION[stage]) {
    return resolveSectionAfterInitialReview(STAGE_TO_SECTION[stage], request);
  }

  if (hasV2) {
    return resolveSectionAfterInitialReview(
      isInitialReviewComplete(request)
        ? SECTION_AFTER_INITIAL_REVIEW
        : "initial-review",
      request
    );
  }

  const confirm = normalizeConfirm(request);

  if (confirm === "notchecked" || confirm === "0" || confirm === "") {
    return "initial-review";
  }

  if (confirm === "completed") {
    return "financial";
  }

  if (isInitialReviewComplete(request)) {
    return SECTION_AFTER_INITIAL_REVIEW;
  }

  return "initial-review";
};

export const resolveActiveSectionIndex = (sectionId) => {
  const idx = WORKSPACE_SECTION_IDS.indexOf(sectionId);
  return idx >= 0 ? idx : 0;
};

export const resolveProgressFromSummary = (summary, request) => {
  const sectionId = mapWorkflowStageToSectionId(summary, request);
  return {
    sectionId,
    activeStepIndex: resolveActiveSectionIndex(sectionId),
  };
};

export const resolveWorkspaceNavigation = ({
  summary,
  request,
  currentSectionId,
  respectExplicitSection = false,
}) => {
  const progress = resolveProgressFromSummary(summary, request);
  const { sectionId, activeStepIndex } = progress;

  // User opened a specific section (e.g. edit → initial-review) — do not auto-advance.
  if (respectExplicitSection && currentSectionId) {
    return {
      sectionId: currentSectionId,
      activeStepIndex: resolveActiveSectionIndex(currentSectionId),
      shouldNavigate: false,
    };
  }

  const reviewComplete = isInitialReviewComplete(request);

  // Stale deep-link (e.g. list → section=initial-review): advance approved cases.
  if (reviewComplete && currentSectionId === "initial-review") {
    return {
      sectionId,
      activeStepIndex,
      shouldNavigate: sectionId !== "initial-review",
    };
  }

  const currentIndex = resolveActiveSectionIndex(currentSectionId);
  const shouldNavigate = activeStepIndex > currentIndex;

  return {
    sectionId,
    activeStepIndex,
    shouldNavigate,
  };
};

export const applyWorkflowProgress = ({
  summary,
  request,
  currentSectionId,
  navigateToStage = true,
  respectExplicitSection = false,
}) => {
  const navigation = resolveWorkspaceNavigation({
    summary,
    request,
    currentSectionId,
    respectExplicitSection: respectExplicitSection || !navigateToStage,
  });

  return {
    sectionId: navigation.sectionId,
    activeStepIndex: navigation.activeStepIndex,
    shouldNavigate: navigation.shouldNavigate,
  };
};
